import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";

/**
 * Orchestrates the Withdraw reconfirm dialog: which reason list to show
 * (keyed off the property's current status) and the async submit call,
 * which differs per page (plain CRM Property vs. RealEstateProperty, with
 * or without a linked REA listing) - the caller supplies that via
 * openWithdraw's onSubmit so this hook stays agnostic to which endpoint
 * is actually hit.
 */
export default function useWithdrawProperty() {
    const [statusName, setStatusName] = useState(null);
    const [loading, setLoading] = useState(false);
    const onSubmitRef = useRef(null);

    const openWithdraw = useCallback((currentStatusName, onSubmit) => {
        onSubmitRef.current = onSubmit;
        setStatusName(currentStatusName || "");
    }, []);

    const closeWithdraw = useCallback(() => {
        if (loading) return;
        setStatusName(null);
        onSubmitRef.current = null;
    }, [loading]);

    const confirmWithdraw = useCallback(async (reason, reasonNote) => {
        if (!onSubmitRef.current) return;
        setLoading(true);
        try {
            await onSubmitRef.current(reason, reasonNote);
            setStatusName(null);
            onSubmitRef.current = null;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to withdraw property");
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        isWithdrawModalOpen: statusName !== null,
        withdrawStatusName: statusName,
        isWithdrawing: loading,
        openWithdraw,
        closeWithdraw,
        confirmWithdraw,
    };
}
